/**
 * Single source of truth for registration wizard step <-> URL slug mapping, and
 * the server `registration_step` enum -> slug mapping used to reconcile the URL
 * to the API session on load. The URL is the source of truth for the current
 * step; these helpers translate between the URL, the UI step indices, and the
 * server enum.
 */

import {
  REGISTRATION_DIVISION_PARAM,
  REGISTRATION_TEAM_PARAM,
  REGISTRATION_ROLE_PARAM,
} from '@sportninja/common/features/registration/registrationUrlParams'
import { PAYMENT_PRE_SUBMIT_STEP } from '@sportninja/common/features/registration/paymentGate'

// UI step indices. Mirrors REGISTRATION_STEPS in steps/Main.jsx / index.jsx.
export const REGISTRATION_STEPS = {
  SELECT_YOUR_DIVISION: 0,
  SIGN_UP: 1,
  LOGIN: 2,
  TEAM_REGISTER: 3,
  REGISTRATION_PAYMENT: 4,
  CONFIRM_YOUR_EMAIL: 5,
  REGISTER_REVIEW: 6,
  SELECT_YOUR_ROLE: 7,
  WAIVER: 8,
} as const

// step index -> URL slug
const STEP_TO_SLUG: Record<number, string> = {
  [REGISTRATION_STEPS.SELECT_YOUR_DIVISION]: 'select-division',
  [REGISTRATION_STEPS.SIGN_UP]: 'sign-up',
  [REGISTRATION_STEPS.LOGIN]: 'login',
  [REGISTRATION_STEPS.TEAM_REGISTER]: 'register-entity',
  [REGISTRATION_STEPS.REGISTRATION_PAYMENT]: 'payment',
  [REGISTRATION_STEPS.CONFIRM_YOUR_EMAIL]: 'confirm-email',
  [REGISTRATION_STEPS.REGISTER_REVIEW]: 'review',
  [REGISTRATION_STEPS.SELECT_YOUR_ROLE]: 'select-role',
  [REGISTRATION_STEPS.WAIVER]: 'waiver',
}

// URL slug -> step index
const SLUG_TO_STEP: Record<string, number> = Object.entries(
  STEP_TO_SLUG
).reduce((acc, [step, slug]) => {
  acc[slug] = Number(step)
  return acc
}, {} as Record<string, number>)

// Server `registration_step` enum value -> UI step index. Encodes the collapsing
// the old index.jsx switch performed (validate_email -> confirm-email screen,
// confirm -> review screen). `created`/unknown default to select-division.
const SERVER_STEP_TO_STEP: Record<string, number> = {
  select_division: REGISTRATION_STEPS.SELECT_YOUR_DIVISION,
  registration_type: REGISTRATION_STEPS.SELECT_YOUR_ROLE,
  team_register: REGISTRATION_STEPS.TEAM_REGISTER,
  payment: REGISTRATION_STEPS.REGISTRATION_PAYMENT,
  // Not a real progression step: the payment gate PUTs `payment_pre_submit` to
  // re-validate the session before handing a card to Stripe/SportsPay (see
  // hooks/usePaymentGate.ts). The server currently persists it, so a registrant
  // who reloads after pressing PAY NOW would otherwise fall through to the
  // default and be thrown back to select-division with the whole flow already
  // filled in. Map it to the payment screen they were actually on.
  [PAYMENT_PRE_SUBMIT_STEP]: REGISTRATION_STEPS.REGISTRATION_PAYMENT,
  validate_email: REGISTRATION_STEPS.CONFIRM_YOUR_EMAIL,
  confirm: REGISTRATION_STEPS.REGISTER_REVIEW,
  waiver: REGISTRATION_STEPS.WAIVER,
  // NOTE: `sign_up` is intentionally NOT mapped. The old index.jsx switch had no
  // `case RegistrationSteps.SIGN_UP`, so a persisted server step of `sign_up`
  // fell through to default -> select-division. We preserve that exactly (this
  // refactor is behavior-neutral). If the backend emits `sign_up` and resuming
  // on the sign-up screen becomes desired, add it as a deliberate product change.
}

export function slugForStep(step: number): string {
  return (
    STEP_TO_SLUG[step] ?? STEP_TO_SLUG[REGISTRATION_STEPS.SELECT_YOUR_DIVISION]
  )
}

export function stepForSlug(slug: string | undefined): number | null {
  if (!slug) {
    return null
  }
  const step = SLUG_TO_STEP[slug]
  return step === undefined ? null : step
}

export function slugForServerStep(serverStep: string | undefined): string {
  const mapped = serverStep ? SERVER_STEP_TO_STEP[serverStep] : undefined
  const step = mapped ?? REGISTRATION_STEPS.SELECT_YOUR_DIVISION
  return slugForStep(step)
}

/**
 * Query params that must survive step navigation, as an allow-list.
 *
 * The deep-link params (`division_id`, `team_id`, and `type` for the role) have
 * to reach steps the reader has not arrived at yet, but every in-wizard
 * navigation is a `history.replace`/`push` of a freshly built path, which drops
 * the query string. Forwarding them keeps the deep link working AND keeps the
 * address bar shareable at any step. `type` additionally has to persist because
 * it is a standing constraint, not a one-shot pre-select: drop it and the role
 * it locks out becomes selectable again on the next step change.
 *
 * Taken from the same constants the links are built from, so a rename cannot
 * silently strand a param here.
 *
 * An allow-list, NOT the whole search string: `nreg` and
 * `registration_session_uid` are deliberately one-shot — they are read once on
 * load, and carrying them forward would re-assert "start a new session" on
 * every step change.
 */
const PRESERVED_PARAMS = [
  REGISTRATION_DIVISION_PARAM,
  REGISTRATION_TEAM_PARAM,
  REGISTRATION_ROLE_PARAM,
] as const

/**
 * The `?…` suffix to carry onto the next step's path, or '' when there is
 * nothing to preserve. Pass `history.location.search`.
 */
export function preserveRegistrationParams(search?: string): string {
  if (!search) {
    return ''
  }
  const current = new URLSearchParams(search)
  const kept = new URLSearchParams()
  PRESERVED_PARAMS.forEach((key) => {
    const value = current.get(key)
    if (value) {
      kept.set(key, value)
    }
  })
  const qs = kept.toString()
  return qs ? `?${qs}` : ''
}

/** Base wizard path for a registration, preserved params included. */
export function registrationBasePath(id: string, search?: string): string {
  return `/registration/${id}${preserveRegistrationParams(search)}`
}

/** Path for an already-resolved slug (e.g. one from `slugForServerStep`). */
export function registrationSlugPath(
  id: string,
  slug: string,
  search?: string
): string {
  return `/registration/${id}/${slug}${preserveRegistrationParams(search)}`
}

export function registrationStepPath(
  id: string,
  step: number,
  search?: string
): string {
  return registrationSlugPath(id, slugForStep(step), search)
}
